import { Vue, Component } from 'vue-property-decorator';
import { inject } from 'inversify-props';
import { InjectionIdEnum } from '@/enums/injection-id.enum';
import { ClientTypeEnum } from '@/enums/client-type.enum';
import CRMEvoTalksService, {
  ICRMEvoTalksStatusResult,
  ICRMEvoTalksSyncClienteEmpresaResult,
} from '@/services/crm/evotalks.service';
import ClientModel from '@/models/crm/client.model';
import SettingsModel from '@/models/crm/settings.model';

@Component({})
export default class EvoTalksMixin extends Vue {
  @inject(InjectionIdEnum.CRMEvoTalksService)
  private evotalksService!: CRMEvoTalksService;

  evotalksSyncEmpresaLoading = false;

  evotalksSyncContatosLoading = false;

  evotalksStatus: ICRMEvoTalksStatusResult = {
    empresaSincronizada: false,
    evotalksClientId: null,
    totalContatos: 0,
    contatosSincronizados: 0,
    ultimaSincronizacaoEmpresa: null,
    ultimaSincronizacaoContatos: null,
  };

  get evotalksClient(): ClientModel | null {
    return (this as any).client;
  }

  get evotalksClientType(): ClientTypeEnum {
    return (this as any).clientType;
  }

  get evotalksSettings(): SettingsModel {
    return (this as any).settings;
  }

  get showEvoTalksActions(): boolean {
    return this.evotalksSettings?.isWhatsAppEvoTalksAtivo
      && (this.evotalksClientType === ClientTypeEnum.Client
        || this.evotalksClientType === ClientTypeEnum.Prospect);
  }

  get syncEntityActionLabel(): string {
    return this.evotalksClientType === ClientTypeEnum.Client
      ? 'Sincronizar Cliente no EvoTalks (BETA)'
      : 'Sincronizar Prospect no EvoTalks (BETA)';
  }

  get isEntitySynced(): boolean {
    return this.evotalksStatus.empresaSincronizada;
  }

  get areContactsSynced(): boolean {
    return this.evotalksStatus.totalContatos > 0
      && this.evotalksStatus.contatosSincronizados >= this.evotalksStatus.totalContatos;
  }

  get syncEntityTooltip(): string {
    return this.formatLastSyncTooltip(this.evotalksStatus.ultimaSincronizacaoEmpresa);
  }

  get syncContactsTooltip(): string {
    return this.formatLastSyncTooltip(this.evotalksStatus.ultimaSincronizacaoContatos);
  }

  resetCRMEvoTalksStatus(): void {
    this.evotalksStatus = {
      empresaSincronizada: false,
      evotalksClientId: null,
      totalContatos: 0,
      contatosSincronizados: 0,
      ultimaSincronizacaoEmpresa: null,
      ultimaSincronizacaoContatos: null,
    };
  }

  async loadCRMEvoTalksStatus(): Promise<void> {
    const client = this.evotalksClient;
    if (!client?.codCliente) return;
    try {
      if (this.evotalksClientType === ClientTypeEnum.Client) {
        const status = await this.evotalksService.getClienteStatus(client.cnpjCpf ?? null, null);
        this.evotalksStatus = {
          empresaSincronizada: status.clienteSincronizado,
          evotalksClientId: status.evoClientId,
          totalContatos: status.totalContatos ?? 0,
          contatosSincronizados: status.contatosSincronizados ?? 0,
          ultimaSincronizacaoEmpresa: status.ultimaSincronizacaoEmpresa ?? null,
          ultimaSincronizacaoContatos: status.ultimaSincronizacaoContatos ?? null,
        };
      } else {
        this.evotalksStatus = await this.evotalksService.getStatus(null, Number(client.codCliente));
      }
    } catch {
      // silencioso — status nao e critico
    }
  }

  // eslint-disable-next-line class-methods-use-this
  formatLastSyncTooltip(lastSync: string | null): string {
    if (!lastSync) {
      return 'Sincronizado';
    }

    const date = new Date(lastSync);
    if (Number.isNaN(date.getTime())) {
      return 'Sincronizado';
    }

    return `Ultima sincronizacao: ${date.toLocaleString('pt-BR')}`;
  }

  async onSyncEmpresaCRMEvoTalks(): Promise<void> {
    const client = this.evotalksClient;
    if (!client) return;
    this.evotalksSyncEmpresaLoading = true;
    try {
      if (this.evotalksClientType === ClientTypeEnum.Client) {
        const result: ICRMEvoTalksSyncClienteEmpresaResult = await this.evotalksService
          .syncClienteEmpresa(client.cnpjCpf ?? null, null);
        if (result.empresaSincronizada) {
          this.$notify.success('Empresa sincronizada com Evo Talks');
        }
      } else {
        const result = await this.evotalksService.syncEmpresa(null, Number(client.codCliente));
        if (result.synced) {
          this.$notify.success('Empresa sincronizada com Evo Talks');
        }
      }
      await this.loadCRMEvoTalksStatus();
    } catch (err) {
      this.$notify.error('Erro ao sincronizar com Evo Talks');
    } finally {
      this.evotalksSyncEmpresaLoading = false;
    }
  }

  async onSyncContatosCRMEvoTalks(): Promise<void> {
    const client = this.evotalksClient;
    if (!client) return;
    this.evotalksSyncContatosLoading = true;
    try {
      if (this.evotalksClientType === ClientTypeEnum.Client) {
        const result = await this.evotalksService.syncClienteContatos(client.cnpjCpf ?? null, null);
        this.$notify.success(`Contatos sincronizados: ${result.sincronizados}/${result.total}`);
      } else {
        const result = await this.evotalksService.syncContatos(null, Number(client.codCliente));
        this.$notify.success(`Contatos sincronizados: ${result.sincronizados}/${result.total}`);
      }
      await this.loadCRMEvoTalksStatus();
    } catch (err) {
      this.$notify.error('Erro ao sincronizar contatos');
    } finally {
      this.evotalksSyncContatosLoading = false;
    }
  }
}
